import { ApolloCache, gql, TypedDocumentNode } from "@apollo/client";
import { JOB_APPLICANT_COUNT_ESTIMATE_FRAGMENT } from "@/features/jobs/applicant-count-estimate/queries";
import {
  JOB_APPLICATIONS_FRAGMENT,
  JOB_APPLICATIONS_WITH_REVIEWS_FRAGMENT,
  JOB_INTERACTIONS_FRAGMENT
} from "./fragments";

export const JOB_POST_DETAILS_QUERY = gql`
  query jobPost($jobPostId: ID!) {
    jobPost(id: $jobPostId) {
      id
      name
      createdAt
      closedDate
      updatedAt
      facility {
        id
        name
        mainAddress {
          id
          address1
          address2
          city
          state
          postalCode
          countryCode
        }
      }
      section {
        id
        name
      }
      currentCompensation
      previousCompensation
      numberOfApplicants
      priority
      status
      subject
      name
      agreedToTermsOfService
      serviceLevel
      compensation {
        baseMaximum
        baseMinimum
        totalAnticipated
        currencyUnit
      }
      benefits
      additionalIncome
      pointOfContact {
        name
        email
        title
        phone
      }
      owner {
        id
        email
        name
        photo
      }
      owners {
        id
        email
        name
        photo
      }
      employer {
        name
        email
        phone
        pgaId
      }
      essentialResponsibilities
      category {
        name
        area
        type
      }
      overview
      pgaOrgUrl
      reviewRequestSent
      requiredEducation
      requiredExperienceInYears
      requiredExperienceDescription
      hideCompensationFromJobBoard
      applicantGroup
      applyVia
      paySchedule
      positionType
      stage
      optInToCareerConsultantAssistance
      isHireePgaPro
      isNetNewPosition
      hiree {
        id
        pgaId
        firstName
        lastName
        email
      }
      otherSections
      numberOfApplicants
      otherStates
      submissionDeadline
      goLiveTime
      jobPostPublishTime
      additionalPgaEducation
      externalJobUrl
      stopCaseCloseNotifications
      consultantVisitedFacility
      isFacilityConversion
      isPlacementConversion
      hiredCompensationAmount
      hiredCompensationSchedule
      closeComments
      sendEmployerSurvey
      applicationFormUrl
      jobApplicantsListUrl
      applicationForm {
        id
        isFormLocked
        referralSourceOptions
        questions {
          id
          questionType
          content
          position
          options {
            id
            content
            position
          }
        }
      }
      estimatedApplicantCount {
        ...JobApplicantCountEstimateFields
      }
    }
  }
  ${JOB_APPLICANT_COUNT_ESTIMATE_FRAGMENT}
`;

export type JobPostViewsResponse = {
  jobPost: {
    id: string;
    views: number | null;
  };
};

export type JobPostViewsVariables = {
  jobPostId: string;
};

export const JOB_POST_VIEWS_QUERY: TypedDocumentNode<JobPostViewsResponse, JobPostViewsVariables> = gql`
  query JobPostViews($jobPostId: ID!) {
    jobPost(id: $jobPostId) {
      id
      views
    }
  }
`;

export const JOB_POST_WITH_INTERACTIONS_QUERY = gql`
  query jobPost($jobPostId: ID!) {
    jobPost(id: $jobPostId) {
      id
      name
      category {
        name
      }
      facility {
        name
      }
      owner {
        name
        email
      }
      owners {
        id
        name
        email
        photo
      }
      interactions {
        ...InteractionsFragment
      }
    }
  }
  ${JOB_INTERACTIONS_FRAGMENT}
`;

export const JOB_POST_WITH_APPLICATIONS_QUERY = gql`
  query JobPostWithApplications($jobPostId: ID!) {
    jobPost(id: $jobPostId) {
      id
      serviceLevel
      applications {
        ...ApplicationsFragment
      }
    }
  }
  ${JOB_APPLICATIONS_FRAGMENT}
`;

export const JOB_POST_WITH_APPLICATION_REVIEWS_QUERY = gql`
  query JobPostWithApplicationReviews($jobPostId: ID!) {
    jobPost(id: $jobPostId) {
      id
      serviceLevel
      applications {
        ...ApplicationsFragment
        ...ApplicationsWithReviewsFragment
      }
    }
  }
  ${JOB_APPLICATIONS_FRAGMENT}
  ${JOB_APPLICATIONS_WITH_REVIEWS_FRAGMENT}
`;

export const CLOSE_JOB_POST_DETAILS_QUERY = gql`
  query closeJobPost($jobPostId: ID!) {
    jobPost(id: $jobPostId) {
      id
      status
      consultantVisitedFacility
      isFacilityConversion
      isPlacementConversion
      isNetNewPosition
      hiredCompensationAmount
      hiredCompensationSchedule
      closeComments
      sendEmployerSurvey
      paySchedule
      compensation {
        totalAnticipated
      }
    }
  }
`;
export const JOBS_SEARCH_QUERY = gql`
  query jobPosts(
    $first: Int
    $last: Int
    $after: String
    $before: String
    $orderBy: BackofficeJobPostOrderByInput
    $input: BackofficeJobPostSearchInput!
  ) {
    jobPosts(first: $first, last: $last, after: $after, before: $before, orderBy: $orderBy, input: $input) {
      nodes {
        id
        createdAt
        facility {
          id
          name
        }
        section {
          id
          name
        }
        owner {
          name
        }
        numberOfApplicants
        status
        name
        serviceLevel
        goLiveTime
        jobPostPublishTime
        compensation {
          totalAnticipated
        }
        pointOfContact {
          name
        }
        employer {
          name
        }
        pgaOrgUrl
        reviewRequestSent
        applyVia
        stage
        submissionDeadline
        externalJobUrl
        isFavorited
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

export const SEND_REVIEW_REQUEST_MUTATION = gql`
  mutation sendJobPostReviewRequest($input: BackofficeSendJobPostReviewRequestInput!) {
    sendJobPostReviewRequest(input: $input) {
      message
      success
    }
  }
`;

export const PATCH_JOB_POST_MUTATION = gql`
  mutation PatchJobPost($input: BackofficePatchJobPostInput!) {
    patchJobPost(input: $input) {
      success
      message
      jobPost {
        id
        stage
        submissionDeadline
      }
    }
  }
`;

export const DELETE_JOB_APPLICATION = gql`
  mutation DeleteJobApplication($applicationId: ID!) {
    deleteJobApplication(applicationId: $applicationId) {
      success
      message
      code
      application {
        id
      }
    }
  }
`;

export const DELETE_JOB_APPLICATION_REVIEW = gql`
  mutation deleteJobApplicationReview($id: ID!) {
    deleteJobApplicationReview(id: $id) {
      success
      message
    }
  }
`;

export const DELETE_JOB_POST_MUTATION = gql`
  mutation DeleteJobPost($jobPostId: ID!) {
    deleteJobPost(jobPostId: $jobPostId) {
      success
      message
    }
  }
`;

export const UPDATE_CLOSE_JOB_MUTATION = gql`
  mutation UpdateCloseJob($input: BackofficeUpdateCloseJobInput!) {
    updateCloseJob(input: $input) {
      success
      message
    }
  }
`;

export const CREATE_JOB_APPLICATION_REVIEW = gql`
  mutation createJobApplicationReview($input: BackofficeJobApplicationReviewInput!) {
    createJobApplicationReview(input: $input) {
      success
      message
    }
  }
`;

export const UPDATE_JOB_APPLICATION_REVIEW = gql`
  mutation updateJobApplicationReview($input: BackofficeJobApplicationReviewInput!) {
    updateJobApplicationReview(input: $input) {
      success
      message
    }
  }
`;

export interface CreateJobApplicationInput {
  jobPostId: string;
  emailAddress?: string;
  phoneNumber?: string;
  website?: string;
  pgaId?: string;
  city?: string;
  state?: string;
  currentOrMostRecentEmployer?: string;
  currentOrMostRecentPosition?: string;
  referralSource?: string[];
  attachments?: Array<{
    type: "resume" | "cover_letter";
    file: File;
  }>;
  answers?: Array<{
    questionId: string;
    optionIds?: string[];
    customAnswer?: string;
  }>;
}

export interface CreateJobApplicationResponse {
  createJobApplication: {
    success: boolean;
    message: string;
  };
}

export const CREATE_JOB_APPLICATION: TypedDocumentNode<
  CreateJobApplicationResponse,
  { input: CreateJobApplicationInput }
> = gql`
  mutation CreateJobApplication($input: BackofficeCreateJobApplicationInput!) {
    createJobApplication(input: $input) {
      success
      message
    }
  }
`;

export const updateCache = (cache: ApolloCache<object>, id: string, fieldName: string, values: any) => {
  return cache.modify({
    id: id,
    fields: {
      [fieldName]: values
    }
  });
};
